import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Redirect, Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Responsive, Menu, Icon, Image, Dropdown } from 'semantic-ui-react';
import i18next from 'i18next';
import { logout, changeLanguage } from '../../actions';
import { hasSuperPermission, hasBusinessPermission, hasLoyaltyPermission, hasBudgetsPermission } from '../../helpers/permissions';

import logoIcon from '../../assets/images/logo.png';
import './AppContainer.scss';

const MIN_WIDTH = 768;

const AppContainer = (props) => {
    const dispatch = useDispatch();
    const token = useSelector(state => state.auth.token);

    function renderCurrentAppItem() {
        const { pathname } = props.location;

        if (pathname.startsWith('/restaurant/') || pathname.startsWith('/chain/') || pathname.startsWith('/business')) {
            return <Menu.Item as={Link} to="/business"><Icon name='food' /></Menu.Item>;
        }
        if (pathname.startsWith('/company/') || pathname.startsWith('/budgets')) {
            return <Menu.Item as={Link} to="/budgets"><Icon name='building' /></Menu.Item>;
        }
        if (pathname.startsWith('/loyaltyProgram/')) {
            return <Menu.Item as={Link} to="/loyalty"><Icon name='gift' /></Menu.Item>;
        }

        return null;
    }

    function renderLanguageMenuItem() {
        const options = i18next.languages.map(lang => ({ text: lang.toUpperCase(), value: lang, flag: lang === 'he' ? 'il' : 'us' }));

        return (
            <Dropdown
                options={options}
                icon={null}
                value={i18next.language}
                onChange={(e, { value }) => {
                    if (i18next.language !== value) {
                        dispatch(changeLanguage(value));
                    }
                }}
                item
                style={{ display: 'flex', flexDirection: 'column-reverse', alignItems: 'center' }}
            />
        );
    }

    function renderAdminMenuItem() {
        if (!hasSuperPermission('panelUsers') && !hasSuperPermission('developer') && !hasSuperPermission('tools')) return null;

        return (
            <Dropdown icon="spy" item style={{ display: 'flex', flexDirection: 'column-reverse', alignItems: 'center' }}>
                <Dropdown.Menu>
                    {hasSuperPermission('panelUsers') ? <Dropdown.Item as={Link} to='/admin/users'>{i18next.t('dashboardUsers')}</Dropdown.Item> : undefined}
                    {hasSuperPermission('developer') ? <Dropdown.Item as={Link} to='/admin/locales'>{i18next.t('localesTitle')}</Dropdown.Item> : undefined}
                    {hasSuperPermission('tools') ? <Dropdown.Item as={Link} to='/admin/tools'>{i18next.t('navToolsTitle')}</Dropdown.Item> : undefined}
                </Dropdown.Menu>
            </Dropdown>
        );
    }

    function renderAppsMenuItem() {
        const menuItems = [];
        if (hasBusinessPermission()) menuItems.push(<Dropdown.Item key={1} as={Link} to='/business'>{i18next.t('businesses')}</Dropdown.Item>);
        if (hasBudgetsPermission()) menuItems.push(<Dropdown.Item key={2} as={Link} to='/budgets'>{i18next.t('userPermissions.budgets')}</Dropdown.Item>);
        if (hasLoyaltyPermission()) menuItems.push(<Dropdown.Item key={3} as={Link} to='/loyalty'>{i18next.t('loyaltyProgram.plural')}</Dropdown.Item>);

        if (menuItems.length <= 1) return null;

        return (
            <Dropdown icon="grid layout" item style={{ display: 'flex', flexDirection: 'column-reverse', alignItems: 'center' }}>
                <Dropdown.Menu>{menuItems}</Dropdown.Menu>
            </Dropdown>
        );
    }

    function renderProfileMenuItem() {
        return (
            <Dropdown icon="user circle" item style={{ display: 'flex', flexDirection: 'column-reverse', alignItems: 'center' }}>
                <Dropdown.Menu>
                    <Dropdown.Item as={Link} to='/user/settings'>{i18next.t('userSettings.self')}</Dropdown.Item>
                    <Dropdown.Item onClick={() => dispatch(logout())}>{i18next.t('logout')}</Dropdown.Item>
                </Dropdown.Menu>
            </Dropdown>
        );
    }

    if (!token) {
        return <Redirect to={{ pathname: '/login' }} />;
    }

    return (
        <div className="app-container">
            {props.children}

            <Responsive
                as={Menu}
                minWidth={MIN_WIDTH}
                fixed="left"
                vertical
                icon='labeled'
                color="blue"
                inverted
                borderless
                style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
                className="app-container-main-nav"
            >
                <Menu.Item as={Link} to={'/'} className="logo-container">
                    <Image fluid src={logoIcon} style={{ width: 50 }} />
                </Menu.Item>

                {renderCurrentAppItem()}

                <div style={{ flex: 1 }} />

                {renderLanguageMenuItem()}
                {renderAdminMenuItem()}
                {renderAppsMenuItem()}
                {renderProfileMenuItem()}
            </Responsive>

            <Responsive
                maxWidth={MIN_WIDTH - 1}
                as={Menu}
                fixed="bottom"
                icon='labeled'
                color="blue"
                inverted
                borderless
                className="app-container-main-nav"
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-around' }}
            >
                {renderCurrentAppItem()}
                {renderAdminMenuItem()}
                {renderAppsMenuItem()}
                {renderProfileMenuItem()}
            </Responsive>

        </div>
    );
};

AppContainer.propTypes = {
    children: PropTypes.any,
};

export default AppContainer;
